import { useState, useRef, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthContext } from '../contexts/AuthContext';
import { useTasks } from '../contexts/TasksContext';
import { Task as ApiTask, executeTask } from '../services/tasksApi';
import { sendMessageStream, IntentResponse, StreamChunk, ExecutionMode } from '../services/api';

// Map UI mode names to API execution modes
const getExecutionMode = (mode: string): ExecutionMode => {
  switch (mode.toLowerCase()) {
    case 'fast': return 'fast';
    default: return 'super';
  }
};

import {
  SpaceBackground,
  Header,
  ChatBar,
  BottomNav,
  Sidebar,
  SettingsPanel,
  PageTitle,
  GlassCard,
  StatusDot,
  AtomIcon,
  ChevronRight,
  ChevronLeft,
  MoreVertical,
  Trash2,
  XCircle,
  Edit,
  FolderPlus,
  Plus,
  Pause,
  Play,
  CheckCircle
} from '../components/shared';

// Helper to format date for display
const formatTimeAgo = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);
  
  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins} min ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
  return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
};

// Map API task to UI task format
const mapApiTaskToUiTask = (apiTask: ApiTask, index: number): Task => ({
  id: index + 1,
  task_id: apiTask.task_id,
  name: apiTask.title,
  status: apiTask.status as Task['status'],  // Backend now sends matching status values
  lastUpdate: formatTimeAgo(apiTask.updated_at),
  frequency: apiTask.metadata?.frequency as string || 'once'
});

interface Task {
  id: number;
  task_id?: string;
  name: string;
  status: 'running' | 'pending' | 'cancelled' | 'completed' | 'paused';
  lastUpdate: string;
  frequency?: string;
}

type TaskCategory = 'running' | 'pending' | 'completed' | 'cancelled' | 'paused';

// Dropdown menu for task actions
const TaskDropdownMenu = ({
  isOpen,
  onClose,
  task,
  onExecute,
  onRestart,
  onPause,
  onComplete,
  onUpdate,
  onCancel,
  onDelete,
  onAddToProject,
  position
}: {
  isOpen: boolean;
  onClose: () => void;
  task: Task | null;
  onExecute: () => void;
  onRestart: () => void;
  onPause: () => void;
  onComplete: () => void;
  onUpdate: () => void;
  onCancel: () => void;
  onDelete: () => void;
  onAddToProject: () => void;
  position: { top: number; right: number };
}) => {
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onClose]);

  if (!isOpen || !task) return null;

  return (
    <div
      ref={menuRef}
      className="absolute z-[100] w-48 bg-black/95 backdrop-blur-xl border border-cyan-400/30 rounded-xl overflow-hidden shadow-[0_0_30px_rgba(34,211,238,0.3)]"
      style={{ top: position.top, right: position.right }}
    >
      {/* Execute option - for pending tasks (sends to AI pipeline) */}
      {task.status === 'pending' && (
        <button
          onClick={() => { onExecute(); onClose(); }}
          className="w-full text-left px-4 py-3 text-sm text-cyan-400/90 hover:bg-cyan-400/10 hover:text-cyan-300 flex items-center space-x-3 transition-all"
        >
          <Play className="w-4 h-4" />
          <span>Execute task</span>
        </button>
      )}

      {/* Restart option - for cancelled, completed, and paused */}
      {(task.status === 'cancelled' || task.status === 'completed' || task.status === 'paused') && (
        <button
          onClick={() => { onRestart(); onClose(); }}
          className="w-full text-left px-4 py-3 text-sm text-green-400/90 hover:bg-green-400/10 hover:text-green-300 flex items-center space-x-3 transition-all"
        >
          <Play className="w-4 h-4" />
          <span>Restart task</span>
        </button>
      )}

      {/* Pause option - for running tasks */}
      {task.status === 'running' && (
        <button
          onClick={() => { onPause(); onClose(); }}
          className="w-full text-left px-4 py-3 text-sm text-orange-400/90 hover:bg-orange-400/10 hover:text-orange-300 flex items-center space-x-3 transition-all"
        >
          <Pause className="w-4 h-4" />
          <span>Pause task</span>
        </button>
      )}

      {/* Complete option - for running and pending tasks */}
      {(task.status === 'running' || task.status === 'pending') && (
        <button
          onClick={() => { onComplete(); onClose(); }}
          className="w-full text-left px-4 py-3 text-sm text-green-400/90 hover:bg-green-400/10 hover:text-green-300 flex items-center space-x-3 transition-all"
        >
          <CheckCircle className="w-4 h-4" />
          <span>Complete task</span>
        </button>
      )}

      {/* Update option - for all statuses */}
      <button
        onClick={() => { onUpdate(); onClose(); }}
        className="w-full text-left px-4 py-3 text-sm text-cyan-400/90 hover:bg-cyan-400/10 hover:text-cyan-300 flex items-center space-x-3 transition-all"
      >
        <Edit className="w-4 h-4" />
        <span>Update task</span>
      </button>

      {/* Add to project - for all statuses */}
      <button
        onClick={() => { onAddToProject(); onClose(); }}
        className="w-full text-left px-4 py-3 text-sm text-cyan-400/90 hover:bg-cyan-400/10 hover:text-cyan-300 flex items-center space-x-3 transition-all"
      >
        <FolderPlus className="w-4 h-4" />
        <span>Add to project</span>
      </button>

      {/* Cancel option - for running and pending */}
      {(task.status === 'running' || task.status === 'pending') && (
        <button
          onClick={() => { onCancel(); onClose(); }}
          className="w-full text-left px-4 py-3 text-sm text-red-400/90 hover:bg-red-400/10 hover:text-red-300 flex items-center space-x-3 transition-all border-t border-cyan-400/10"
        >
          <XCircle className="w-4 h-4" />
          <span>Cancel task</span>
        </button>
      )}

      {/* Delete option - for all statuses */}
      <button
        onClick={() => { onDelete(); onClose(); }}
        className="w-full text-left px-4 py-3 text-sm text-red-400/90 hover:bg-red-400/10 hover:text-red-300 flex items-center space-x-3 transition-all border-t border-cyan-400/10"
      >
        <Trash2 className="w-4 h-4" />
        <span>Delete task</span>
      </button>
    </div>
  );
};

// New Task Button Component
const NewTaskButton = ({ onClick }: { onClick: () => void }) => (
  <button
    onClick={onClick}
    className="aspect-square flex flex-col items-center justify-center p-3 rounded-xl border backdrop-blur-xl transition-all hover:scale-[1.02] bg-cyan-400/5 border-cyan-400/30 border-dashed hover:border-cyan-400/50 hover:bg-cyan-400/10 hover:shadow-[0_0_20px_rgba(34,211,238,0.2)] group"
  >
    <div className="w-10 h-10 rounded-full bg-cyan-400/10 border border-cyan-400/30 flex items-center justify-center group-hover:bg-cyan-400/20 group-hover:border-cyan-400/50 transition-all mb-2">
      <Plus className="w-5 h-5 text-cyan-400" />
    </div>
    <h3 className="text-cyan-400 font-semibold text-[10px] uppercase tracking-wider text-center">New Task</h3>
  </button>
);

// Category Box Component
const CategoryBox = ({ 
  title, 
  count, 
  color, 
  onClick,
  isActive
}: { 
  title: string; 
  count: number; 
  color: string;
  onClick: () => void;
  isActive: boolean;
}) => {
  const colorClasses: Record<string, { bg: string; border: string; shadow: string; dot: string }> = {
    green: {
      bg: 'bg-green-400/10',
      border: 'border-green-400/30',
      shadow: 'shadow-[0_0_20px_rgba(74,222,128,0.2)]',
      dot: 'bg-green-400 shadow-[0_0_8px_rgba(74,222,128,0.8)]'
    },
    yellow: {
      bg: 'bg-yellow-400/10',
      border: 'border-yellow-400/30',
      shadow: 'shadow-[0_0_20px_rgba(250,204,21,0.2)]',
      dot: 'bg-yellow-400 shadow-[0_0_8px_rgba(250,204,21,0.8)]'
    },
    cyan: {
      bg: 'bg-cyan-400/10',
      border: 'border-cyan-400/30',
      shadow: 'shadow-[0_0_20px_rgba(34,211,238,0.2)]',
      dot: 'bg-cyan-400 shadow-[0_0_8px_rgba(34,211,238,0.8)]'
    },
    red: {
      bg: 'bg-red-400/10',
      border: 'border-red-400/30',
      shadow: 'shadow-[0_0_20px_rgba(248,113,113,0.2)]',
      dot: 'bg-red-400 shadow-[0_0_8px_rgba(248,113,113,0.8)]'
    },
    orange: {
      bg: 'bg-orange-400/10',
      border: 'border-orange-400/30',
      shadow: 'shadow-[0_0_20px_rgba(251,146,60,0.2)]',
      dot: 'bg-orange-400 shadow-[0_0_8px_rgba(251,146,60,0.8)]'
    },
  };

  const colors = colorClasses[color] || colorClasses.cyan;

  return (
    <button
      onClick={onClick}
      className={`aspect-square flex flex-col items-center justify-center p-3 rounded-xl border backdrop-blur-xl transition-all hover:scale-[1.02] ${colors.bg} ${colors.border} ${isActive ? colors.shadow : ''}`}
    >
      <div className={`w-3 h-3 rounded-full ${colors.dot} mb-1`} />
      <span className="text-2xl font-bold text-white mb-0.5">{count}</span>
      <h3 className="text-cyan-300 font-semibold text-[10px] uppercase tracking-wider text-center">{title}</h3>
    </button>
  );
};

const TaskCard = ({
  task,
  onMenuOpen,
  onClick,
  menuOpen,
  onMenuClose,
  onExecute,
  onRestart,
  onPause,
  onComplete,
  onUpdate,
  onCancel,
  onDelete,
  onAddToProject
}: {
  task: Task;
  onMenuOpen: (e: React.MouseEvent) => void;
  onClick: () => void;
  menuOpen: boolean;
  onMenuClose: () => void;
  onExecute: () => void;
  onRestart: () => void;
  onPause: () => void;
  onComplete: () => void;
  onUpdate: () => void;
  onCancel: () => void;
  onDelete: () => void;
  onAddToProject: () => void;
}) => {
  return (
    <div className="relative">
      <GlassCard
        className="p-4 cursor-pointer hover:scale-[1.02] transition-transform"
        onClick={onClick}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <StatusDot status={task.status} />
            <div>
              <h3 className="text-cyan-300 font-semibold text-base drop-shadow-[0_0_3px_rgba(34,211,238,0.3)]">
                {task.name}
              </h3>
              <p className="text-cyan-400/50 text-xs mt-1">
                {task.frequency && task.frequency !== 'once' && (
                  <span className="text-cyan-400/70 mr-2 capitalize">{task.frequency}</span>
                )}
                {task.lastUpdate}
              </p>
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <button
              onClick={(e) => { e.stopPropagation(); onMenuOpen(e); }}
              className="p-2 rounded-full hover:bg-cyan-400/10 text-cyan-400/60 hover:text-cyan-300 transition-colors"
            >
              <MoreVertical className="w-5 h-5" />
            </button>
            <ChevronRight className="w-5 h-5 text-cyan-400/40" />
          </div>
        </div>
      </GlassCard>

      {/* Dropdown menu */}
      <TaskDropdownMenu
        isOpen={menuOpen}
        onClose={onMenuClose}
        task={task}
        onExecute={onExecute}
        onRestart={onRestart}
        onPause={onPause}
        onComplete={onComplete}
        onUpdate={onUpdate}
        onCancel={onCancel}
        onDelete={onDelete}
        onAddToProject={onAddToProject}
        position={{ top: 50, right: 40 }}
      />
    </div>
  );
};

// New Task Conversation View
const NewTaskView = ({ onClose, onCreateTask }: { onClose: () => void; onCreateTask: (name: string) => void }) => {
  const [inputText, setInputText] = useState('');
  const [chatMode, setChatMode] = useState('Super');
  const [isLoading, setIsLoading] = useState(false);
  const [streamingContent, setStreamingContent] = useState('');
  const [conversation, setConversation] = useState<{role: string; message: string}[]>([
    { role: 'assistant', message: 'What task would you like me to do?' }
  ]);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [taskCreated, setTaskCreated] = useState(false);

  const handleSend = async () => {
    if (inputText.trim() && !isLoading) {
      const userMessage = inputText.trim();
      setConversation(prev => [...prev, { role: 'user', message: userMessage }]);
      setInputText('');

      // Create the task in Firestore
      if (!taskCreated) {
        onCreateTask(userMessage);
        setTaskCreated(true);
      }

      // Get real AI response about the task
      setIsLoading(true);
      setStreamingContent('');

      const taskPrompt = `Create a task: "${userMessage}". Acknowledge the task, briefly describe what you'll do, and ask if there are any specific requirements or deadlines.`;
      const history = conversation.map(msg => ({
        role: msg.role as 'user' | 'assistant',
        content: msg.message
      }));

      let fullResponse = '';

      try {
        await sendMessageStream(
          taskPrompt,
          { executionMode: getExecutionMode(chatMode) },
          history,
          (chunk: StreamChunk) => {
            if (chunk.type === 'content') {
              fullResponse += chunk.data;
              setStreamingContent(fullResponse);
            }
          },
          (result: IntentResponse) => {
            const assistantContent = result.result as string || fullResponse || `I've created the task "${userMessage}". I'll start analyzing what's needed.`;
            setConversation(prev => [...prev, { role: 'assistant', message: assistantContent }]);
            setStreamingContent('');
            setIsLoading(false);
          },
          (error: Error) => {
            console.error('[NewTask] AI error:', error);
            // Fallback — task is already created, just acknowledge
            setConversation(prev => [...prev, {
              role: 'assistant',
              message: `I've created the task "${userMessage}". I'll start working on it right away.`
            }]);
            setStreamingContent('');
            setIsLoading(false);
          }
        );
      } catch (error) {
        console.error('[NewTask] Send error:', error);
        setConversation(prev => [...prev, {
          role: 'assistant',
          message: `Task "${userMessage}" has been created. I'll analyze the requirements and keep you updated.`
        }]);
        setStreamingContent('');
        setIsLoading(false);
      }
    }
  };

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [conversation]);

  return (
    <div className="fixed inset-0 z-[70] bg-black font-sans">
      <SpaceBackground />

      <div className="relative z-10 flex flex-col h-full">
        {/* Header */}
        <header className="p-6 flex items-center border-b border-cyan-400/10">
          <button
            onClick={onClose}
            className="p-3 rounded-full bg-cyan-500/[0.05] hover:bg-cyan-400/[0.1] backdrop-blur-3xl border border-cyan-400/30 shadow-[0_0_15px_rgba(34,211,238,0.1)] transition-all hover:scale-105 active:scale-95 group mr-6"
          >
            <ChevronLeft className="w-6 h-6 text-cyan-400 group-hover:text-cyan-200 drop-shadow-[0_0_5px_rgba(34,211,238,0.5)]" />
          </button>

          <div className="flex items-center space-x-3">
            <AtomIcon className="w-8 h-8 text-cyan-400 drop-shadow-[0_0_10px_rgba(34,211,238,0.8)] animate-spin-slow" />
            <h1 className="text-2xl font-bold text-cyan-300 tracking-tight drop-shadow-[0_0_5px_rgba(34,211,238,0.5)] font-sans">
              New Task
            </h1>
          </div>

          <div className="ml-auto">
            <AtomIcon className="w-12 h-12 text-cyan-400 drop-shadow-[0_0_15px_rgba(34,211,238,0.8)] animate-spin-slow" />
          </div>
        </header>

        {/* Chat Area */}
        <div 
          ref={scrollRef}
          className="flex-1 overflow-y-auto p-6"
          style={{ 
            paddingBottom: 200,
            maskImage: 'linear-gradient(to bottom, black 0%, black calc(100% - 200px), transparent calc(100% - 120px))',
            WebkitMaskImage: 'linear-gradient(to bottom, black 0%, black calc(100% - 200px), transparent calc(100% - 120px))'
          }}
        >
          <div className="max-w-3xl mx-auto space-y-4">
            {conversation.map((msg, i) => (
              <div 
                key={i} 
                className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div 
                  className={`max-w-[80%] p-4 rounded-2xl ${
                    msg.role === 'user' 
                      ? 'bg-cyan-400/20 border border-cyan-400/30 text-cyan-100' 
                      : 'bg-cyan-400/5 border border-cyan-400/10 text-cyan-300'
                  }`}
                >
                  {msg.role === 'assistant' && (
                    <div className="flex items-center space-x-2 mb-2">
                      <AtomIcon className="w-4 h-4 text-cyan-400" />
                      <span className="text-xs text-cyan-400/70 font-semibold">SuperJarvis</span>
                    </div>
                  )}
                  <p className="text-sm leading-relaxed">{msg.message}</p>
                </div>
              </div>
            ))}
            {streamingContent && (
              <div className="flex justify-start">
                <div className="max-w-[80%] p-4 rounded-2xl bg-cyan-400/5 border border-cyan-400/10 text-cyan-300">
                  <div className="flex items-center space-x-2 mb-2">
                    <AtomIcon className="w-4 h-4 text-cyan-400 animate-spin" />
                    <span className="text-xs text-cyan-400/70 font-semibold">SuperJarvis</span>
                  </div>
                  <p className="text-sm leading-relaxed">{streamingContent}</p>
                </div>
              </div>
            )}
            {isLoading && !streamingContent && (
              <div className="flex justify-start">
                <div className="max-w-[80%] p-4 rounded-2xl bg-cyan-400/5 border border-cyan-400/10 text-cyan-300">
                  <div className="flex items-center space-x-2">
                    <AtomIcon className="w-4 h-4 text-cyan-400 animate-spin" />
                    <span className="text-xs text-cyan-400/70">Thinking...</span>
                  </div>
                </div>
              </div>
            )}

          </div>
        </div>

        {/* Bottom Section */}
        <div className="absolute bottom-0 w-full flex flex-col items-center z-30 px-4 pb-4 bg-gradient-to-t from-black via-black/95 to-transparent pt-16">
          <ChatBar 
            inputText={inputText}
            setInputText={setInputText}
            chatMode={chatMode}
            setChatMode={setChatMode}
            onSend={handleSend}
          />
          <BottomNav activeTab="tasks" />
        </div>
      </div>
    </div>
  );
};

// Task Detail View
const TaskDetailView = ({ task, onClose, asyncTasks = [] }: { task: Task; onClose: () => void; asyncTasks?: { task_id: string; intent: string; status: string; total_subtasks: number; completed_subtasks: number; failed_subtasks: number; subtasks: Record<string, { id: string; domain: string; description: string; status: string }> }[] }) => {
  const [inputText, setInputText] = useState('');
  const [chatMode, setChatMode] = useState('Super');
  const [isLoading, setIsLoading] = useState(false);
  const [streamingContent, setStreamingContent] = useState('');

  // Task conversation - starts with context about the task
  const [conversation, setConversation] = useState<{role: string; message: string}[]>([
    { role: 'assistant', message: `Ready to help with "${task.name}". What would you like me to do?` }
  ]);

  const handleSend = async () => {
    if (inputText.trim() && !isLoading) {
      const userMessage = inputText.trim();
      setConversation(prev => [...prev, { role: 'user', message: userMessage }]);
      setInputText('');
      setIsLoading(true);
      setStreamingContent('');

      // Build context with task info
      const taskContext = `[Task: "${task.name}" | Status: ${task.status}] ${userMessage}`;
      const history = conversation.map(msg => ({
        role: msg.role as 'user' | 'assistant',
        content: msg.message
      }));

      let fullResponse = '';

      try {
        await sendMessageStream(
          taskContext,
          { executionMode: getExecutionMode(chatMode) },
          history,
          (chunk: StreamChunk) => {
            if (chunk.type === 'content') {
              fullResponse += chunk.data;
              setStreamingContent(fullResponse);
            }
          },
          (result: IntentResponse) => {
            const assistantContent = result.result as string || fullResponse || 'I apologize, but I could not generate a response.';
            setConversation(prev => [...prev, { role: 'assistant', message: assistantContent }]);
            setStreamingContent('');
            setIsLoading(false);
          },
          (error: Error) => {
            console.error('[TaskDetail] AI error:', error);
            setConversation(prev => [...prev, {
              role: 'assistant',
              message: `I ran into an issue processing that request. ${error.message || 'Please try again.'}`
            }]);
            setStreamingContent('');
            setIsLoading(false);
          }
        );
      } catch (error) {
        console.error('[TaskDetail] Send error:', error);
        setConversation(prev => [...prev, {
          role: 'assistant',
          message: 'Something went wrong connecting to SuperJarvis. Please try again.'
        }]);
        setStreamingContent('');
        setIsLoading(false);
      }
    }
  };

  return (
    <div className="fixed inset-0 z-[70] bg-black font-sans">
      <SpaceBackground />

      <div className="relative z-10 flex flex-col h-full">
        {/* Task Detail Header */}
        <header className="p-6 flex items-center border-b border-cyan-400/10">
          <button
            onClick={onClose}
            className="p-3 rounded-full bg-cyan-500/[0.05] hover:bg-cyan-400/[0.1] backdrop-blur-3xl border border-cyan-400/30 shadow-[0_0_15px_rgba(34,211,238,0.1)] transition-all hover:scale-105 active:scale-95 group mr-6"
          >
            <ChevronLeft className="w-6 h-6 text-cyan-400 group-hover:text-cyan-200 drop-shadow-[0_0_5px_rgba(34,211,238,0.5)]" />
          </button>

          <div className="flex items-center space-x-3">
            <StatusDot status={task.status} />
            <h1 className="text-2xl font-bold text-cyan-300 tracking-tight drop-shadow-[0_0_5px_rgba(34,211,238,0.5)] font-sans">
              {task.name}
            </h1>
          </div>

          <div className="ml-auto">
            <AtomIcon className="w-12 h-12 text-cyan-400 drop-shadow-[0_0_15px_rgba(34,211,238,0.8)] animate-spin-slow" />
          </div>
        </header>

        {/* Async Subtask Progress */}
        {asyncTasks.length > 0 && (
          <div className="mx-6 mt-4 space-y-3">
            {asyncTasks.filter(at => at.status === 'running' || at.status === 'partial').map(at => (
              <div key={at.task_id} className="p-3 bg-cyan-400/5 border border-cyan-400/20 rounded-xl">
                <div className="flex items-center justify-between text-xs mb-2">
                  <span className="text-cyan-400/70 font-medium truncate mr-2">{at.intent}</span>
                  <span className="text-cyan-400/50 shrink-0">{at.completed_subtasks}/{at.total_subtasks} done</span>
                </div>
                <div className="h-1.5 bg-cyan-400/10 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-cyan-400 rounded-full transition-all duration-500"
                    style={{ width: `${at.total_subtasks > 0 ? (at.completed_subtasks / at.total_subtasks) * 100 : 0}%` }}
                  />
                </div>
                {Object.values(at.subtasks).length > 0 && (
                  <div className="mt-2 space-y-1">
                    {Object.values(at.subtasks).map(st => (
                      <div key={st.id} className="flex items-center gap-2 text-xs">
                        <StatusDot status={st.status === 'completed' ? 'completed' : st.status === 'running' ? 'running' : st.status === 'failed' ? 'cancelled' : 'pending'} />
                        <span className="text-cyan-400/60 capitalize">{st.domain}</span>
                        <span className="text-cyan-400/40 truncate">{st.description}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Conversation History */}
        <div
          className="flex-1 overflow-y-auto p-6"
          style={{
            paddingBottom: 200,
            maskImage: 'linear-gradient(to bottom, black 0%, black calc(100% - 200px), transparent calc(100% - 120px))',
            WebkitMaskImage: 'linear-gradient(to bottom, black 0%, black calc(100% - 200px), transparent calc(100% - 120px))'
          }}
        >
          <div className="max-w-3xl mx-auto space-y-4">
            {conversation.map((msg, i) => (
              <div 
                key={i} 
                className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div 
                  className={`max-w-[80%] p-4 rounded-2xl ${
                    msg.role === 'user' 
                      ? 'bg-cyan-400/20 border border-cyan-400/30 text-cyan-100' 
                      : 'bg-cyan-400/5 border border-cyan-400/10 text-cyan-300'
                  }`}
                >
                  {msg.role === 'assistant' && (
                    <div className="flex items-center space-x-2 mb-2">
                      <AtomIcon className="w-4 h-4 text-cyan-400" />
                      <span className="text-xs text-cyan-400/70 font-semibold">SuperJarvis</span>
                    </div>
                  )}
                  <p className="text-sm leading-relaxed">{msg.message}</p>
                </div>
              </div>
            ))}
            {streamingContent && (
              <div className="flex justify-start">
                <div className="max-w-[80%] p-4 rounded-2xl bg-cyan-400/5 border border-cyan-400/10 text-cyan-300">
                  <div className="flex items-center space-x-2 mb-2">
                    <AtomIcon className="w-4 h-4 text-cyan-400 animate-spin" />
                    <span className="text-xs text-cyan-400/70 font-semibold">SuperJarvis</span>
                  </div>
                  <p className="text-sm leading-relaxed">{streamingContent}</p>
                </div>
              </div>
            )}
            {isLoading && !streamingContent && (
              <div className="flex justify-start">
                <div className="max-w-[80%] p-4 rounded-2xl bg-cyan-400/5 border border-cyan-400/10 text-cyan-300">
                  <div className="flex items-center space-x-2">
                    <AtomIcon className="w-4 h-4 text-cyan-400 animate-spin" />
                    <span className="text-xs text-cyan-400/70">Thinking...</span>
                  </div>
                </div>
              </div>
            )}

          </div>
        </div>

        {/* Bottom Section */}
        <div className="absolute bottom-0 w-full flex flex-col items-center z-30 px-4 pb-4 bg-gradient-to-t from-black via-black/95 to-transparent pt-16">
          <ChatBar 
            inputText={inputText}
            setInputText={setInputText}
            chatMode={chatMode}
            setChatMode={setChatMode}
            onSend={handleSend}
          />
          <BottomNav activeTab="tasks" />
        </div>
      </div>
    </div>
  );
};

// Category List View
const CategoryListView = ({
  category,
  tasks,
  onClose,
  onTaskClick,
  onExecute,
  onRestart,
  onPause,
  onComplete,
  onUpdate,
  onCancel,
  onDelete,
  onAddToProject,
  onCreateTask
}: {
  category: TaskCategory;
  tasks: Task[];
  onClose: () => void;
  onTaskClick: (task: Task) => void;
  onExecute: (id: number) => void;
  onRestart: (id: number) => void;
  onPause: (id: number) => void;
  onComplete: (id: number) => void;
  onUpdate: (id: number) => void;
  onCancel: (id: number) => void;
  onDelete: (id: number) => void;
  onAddToProject: (id: number) => void;
  onCreateTask: (name: string) => void;
}) => {
  const [inputText, setInputText] = useState('');
  const [chatMode, setChatMode] = useState('Super');
  const [openMenuId, setOpenMenuId] = useState<number | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const categoryInfo: Record<TaskCategory, { title: string; color: string; dot: string }> = {
    running: { title: 'Running Tasks', color: 'text-green-400', dot: 'bg-green-400 shadow-[0_0_8px_rgba(74,222,128,0.8)]' },
    pending: { title: 'Pending Tasks', color: 'text-yellow-400', dot: 'bg-yellow-400 shadow-[0_0_8px_rgba(250,204,21,0.8)]' },
    paused: { title: 'Paused Tasks', color: 'text-orange-400', dot: 'bg-orange-400 shadow-[0_0_8px_rgba(251,146,60,0.8)]' },
    completed: { title: 'Completed Tasks', color: 'text-cyan-400', dot: 'bg-cyan-400 shadow-[0_0_8px_rgba(34,211,238,0.8)]' },
    cancelled: { title: 'Cancelled Tasks', color: 'text-red-400', dot: 'bg-red-400 shadow-[0_0_8px_rgba(248,113,113,0.8)]' },
  };

  const info = categoryInfo[category];

  const handleSend = () => {
    if (inputText.trim()) {
      onCreateTask(inputText.trim());
      setInputText('');
    }
  };

  return (
    <div className="fixed inset-0 z-[70] bg-black font-sans">
      <SpaceBackground />

      <div className="relative z-10 flex flex-col h-full">
        {/* Header */}
        <header className="p-6 flex items-center border-b border-cyan-400/10">
          <button
            onClick={onClose}
            className="p-3 rounded-full bg-cyan-500/[0.05] hover:bg-cyan-400/[0.1] backdrop-blur-3xl border border-cyan-400/30 shadow-[0_0_15px_rgba(34,211,238,0.1)] transition-all hover:scale-105 active:scale-95 group mr-6"
          >
            <ChevronLeft className="w-6 h-6 text-cyan-400 group-hover:text-cyan-200 drop-shadow-[0_0_5px_rgba(34,211,238,0.5)]" />
          </button>

          <div className="flex items-center space-x-3">
            <div className={`w-4 h-4 rounded-full ${info.dot}`} />
            <h1 className={`text-2xl font-bold ${info.color} tracking-tight drop-shadow-[0_0_5px_rgba(34,211,238,0.5)] font-sans`}>
              {info.title}
            </h1>
            <span className="text-cyan-400/50 text-lg">({tasks.length})</span>
          </div>

          <div className="ml-auto">
            <AtomIcon className="w-12 h-12 text-cyan-400 drop-shadow-[0_0_15px_rgba(34,211,238,0.8)] animate-spin-slow" />
          </div>
        </header>

        {/* Task List with scroll fade */}
        <div 
          ref={scrollContainerRef}
          className="flex-1 overflow-y-auto p-6"
          style={{ 
            maskImage: 'linear-gradient(to bottom, black 0%, black calc(100% - 200px), transparent calc(100% - 120px))',
            WebkitMaskImage: 'linear-gradient(to bottom, black 0%, black calc(100% - 200px), transparent calc(100% - 120px))'
          }}
        >
          <div className="max-w-3xl mx-auto space-y-3">
            {tasks.map(task => (
              <TaskCard
                key={task.id}
                task={task}
                onClick={() => onTaskClick(task)}
                onMenuOpen={(e) => {
                  e.stopPropagation();
                  setOpenMenuId(openMenuId === task.id ? null : task.id);
                }}
                menuOpen={openMenuId === task.id}
                onMenuClose={() => setOpenMenuId(null)}
                onExecute={() => onExecute(task.id)}
                onRestart={() => onRestart(task.id)}
                onPause={() => onPause(task.id)}
                onComplete={() => onComplete(task.id)}
                onUpdate={() => onUpdate(task.id)}
                onCancel={() => onCancel(task.id)}
                onDelete={() => onDelete(task.id)}
                onAddToProject={() => onAddToProject(task.id)}
              />
            ))}
            {tasks.length === 0 && (
              <div className="text-center py-12">
                <p className="text-cyan-400/50">No tasks in this category</p>
              </div>
            )}
          </div>
        </div>

        {/* Bottom Section */}
        <div className="absolute bottom-0 w-full flex flex-col items-center z-30 px-4 pb-4 bg-gradient-to-t from-black via-black/95 to-transparent pt-16">
          <ChatBar 
            inputText={inputText}
            setInputText={setInputText}
            chatMode={chatMode}
            setChatMode={setChatMode}
            onSend={handleSend}
          />
          <BottomNav activeTab="tasks" />
        </div>
      </div>
    </div>
  );
};

export default function MyTasksScreen() {
  const navigate = useNavigate();
  const { user, signOut } = useAuthContext();
  const {
    tasks: apiTasks,
    asyncTasks,
    loading: isLoading,
    error,
    refreshTasks,
    createNewTask,
    updateTaskStatus,
    deleteTaskById,
    clearError
  } = useTasks();

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [inputText, setInputText] = useState('');
  const [chatMode, setChatMode] = useState('Super');
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<TaskCategory | null>(null);
  const [showNewTask, setShowNewTask] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Map API tasks to UI tasks
  const tasks = useMemo(() => apiTasks.map((t, i) => mapApiTaskToUiTask(t, i)), [apiTasks]);

  // Filter tasks by status
  const runningTasks = tasks.filter(t => t.status === 'running');
  const pendingTasks = tasks.filter(t => t.status === 'pending');
  const pausedTasks = tasks.filter(t => t.status === 'paused');
  const completedTasks = tasks.filter(t => t.status === 'completed');
  const cancelledTasks = tasks.filter(t => t.status === 'cancelled');

  const handleDelete = async (id: number) => {
    if (!window.confirm('Delete this task?')) return;
    const task = tasks.find(t => t.id === id);
    if (task?.task_id) {
      await deleteTaskById(task.task_id);
    }
  };

  const handleStatusChange = async (id: number, newStatus: 'running' | 'pending' | 'paused' | 'completed' | 'cancelled') => {
    const task = tasks.find(t => t.id === id);
    if (task?.task_id) {
      await updateTaskStatus(task.task_id, newStatus);
    }
  };

  const handleCancel = (id: number) => handleStatusChange(id, 'cancelled');
  const handleComplete = (id: number) => handleStatusChange(id, 'completed');
  const handleRestart = (id: number) => handleStatusChange(id, 'running');
  const handlePause = (id: number) => handleStatusChange(id, 'paused');

  const handleExecute = async (id: number) => {
    const task = tasks.find(t => t.id === id);
    if (task?.task_id && user?.uid) {
      await updateTaskStatus(task.task_id, 'running');
      const result = await executeTask(task.task_id, user.uid);
      if (result.success) {
        refreshTasks();
      } else {
        alert(`Task execution failed: ${result.error || 'Unknown error'}`);
        refreshTasks();
      }
    }
  };

  const handleUpdate = (id: number) => {
    const task = tasks.find(t => t.id === id);
    if (task) {
      setSelectedTask(task);
    }
  };

  const handleAddToProject = (_id: number) => {
    navigate('/projects');
  };

  const handleCreateTask = async (name: string) => {
    await createNewTask(name);
    // Context auto-adds to list, but refresh for full sync
    refreshTasks();
  };

  const handleSend = () => {
    if (inputText.trim()) {
      handleCreateTask(inputText.trim());
      setInputText('');
    }
  };

  // Show new task view
  if (showNewTask) {
    return <NewTaskView onClose={() => setShowNewTask(false)} onCreateTask={handleCreateTask} />;
  }

  // Show task detail view
  if (selectedTask) {
    return <TaskDetailView task={selectedTask} onClose={() => setSelectedTask(null)} asyncTasks={asyncTasks} />;
  }

  // Show category list view
  if (selectedCategory) {
    const categoryTasks = {
      running: runningTasks,
      pending: pendingTasks,
      paused: pausedTasks,
      completed: completedTasks,
      cancelled: cancelledTasks,
    }[selectedCategory];

    return (
      <CategoryListView
        category={selectedCategory}
        tasks={categoryTasks}
        onClose={() => setSelectedCategory(null)}
        onTaskClick={(task) => setSelectedTask(task)}
        onExecute={handleExecute}
        onRestart={handleRestart}
        onPause={handlePause}
        onComplete={handleComplete}
        onUpdate={handleUpdate}
        onCancel={handleCancel}
        onDelete={handleDelete}
        onAddToProject={handleAddToProject}
        onCreateTask={handleCreateTask}
      />
    );
  }

  return (
    <div className="relative w-full h-screen overflow-hidden bg-black text-cyan-400 font-sans selection:bg-cyan-400/20 selection:text-cyan-100">
      <SpaceBackground />
      
      <div className={`relative z-10 flex flex-col h-full transition-transform duration-500 ${sidebarOpen ? 'translate-x-[320px] scale-95 opacity-50 blur-[2px]' : ''}`}>
        <Header onMenuClick={() => setSidebarOpen(true)} />

        {/* Content Area with scroll fade */}
        <div 
          ref={scrollContainerRef}
          className="flex-1 flex flex-col justify-start pt-28 px-4 overflow-y-auto w-full items-center"
          style={{ 
            paddingBottom: 240,
            maskImage: 'linear-gradient(to bottom, black 0%, black calc(100% - 260px), transparent calc(100% - 180px))',
            WebkitMaskImage: 'linear-gradient(to bottom, black 0%, black calc(100% - 260px), transparent calc(100% - 180px))'
          }}
        >
          <div className="w-full max-w-lg text-left">
            <div className="flex justify-start">
              <PageTitle title="My Tasks" />
            </div>

            {/* Loading indicator */}
            {isLoading && (
              <div className="flex items-center justify-center gap-2 mt-4">
                <div className="w-4 h-4 border-2 border-cyan-400/30 border-t-cyan-400 rounded-full animate-spin" />
                <span className="text-cyan-400/50 text-sm">Loading tasks...</span>
              </div>
            )}

            {/* Error message */}
            {error && (
              <div className="mt-4 p-3 bg-red-500/10 border border-red-500/30 rounded-xl flex items-center justify-between">
                <p className="text-red-400 text-sm">{error}</p>
                <button onClick={clearError} className="text-red-400/50 hover:text-red-300 ml-2">
                  <XCircle className="w-4 h-4" />
                </button>
              </div>
            )}

            {/* Category Boxes with New Task Button - 2 rows of 3 */}
            <div className="grid grid-cols-3 gap-3 mt-6">
              <NewTaskButton onClick={() => setShowNewTask(true)} />
              <CategoryBox
                title="Running"
                count={runningTasks.length}
                color="green"
                onClick={() => setSelectedCategory('running')}
                isActive={runningTasks.length > 0}
              />
              <CategoryBox
                title="Pending"
                count={pendingTasks.length}
                color="yellow"
                onClick={() => setSelectedCategory('pending')}
                isActive={pendingTasks.length > 0}
              />
              <CategoryBox
                title="Paused"
                count={pausedTasks.length}
                color="orange"
                onClick={() => setSelectedCategory('paused')}
                isActive={pausedTasks.length > 0}
              />
              <CategoryBox
                title="Completed"
                count={completedTasks.length}
                color="cyan"
                onClick={() => setSelectedCategory('completed')}
                isActive={completedTasks.length > 0}
              />
              <CategoryBox
                title="Cancelled"
                count={cancelledTasks.length}
                color="red"
                onClick={() => setSelectedCategory('cancelled')}
                isActive={cancelledTasks.length > 0}
              />
            </div>

            {/* Quick summary */}
            <div className="mt-8">
              <h3 className="text-xs font-bold text-cyan-400/50 uppercase tracking-widest mb-4 text-center">
                Quick Overview
              </h3>
              <p className="text-cyan-400/70 text-sm text-center">
                You have <span className="text-green-400 font-semibold">{runningTasks.length} running</span>,
                <span className="text-yellow-400 font-semibold"> {pendingTasks.length} pending</span>,
                <span className="text-orange-400 font-semibold"> {pausedTasks.length} paused</span>,
                and <span className="text-cyan-400 font-semibold">{completedTasks.length} completed</span> tasks.
                {cancelledTasks.length > 0 && (
                  <span className="text-red-400/70"> {cancelledTasks.length} cancelled.</span>
                )}
              </p>
            </div>
          </div>
        </div>

        {/* Bottom Section */}
        <div className="absolute bottom-0 w-full flex flex-col items-center z-30 px-4 pb-4 bg-gradient-to-t from-black via-black/95 to-transparent pt-16">
          <ChatBar 
            inputText={inputText}
            setInputText={setInputText}
            chatMode={chatMode}
            setChatMode={setChatMode}
            onSend={handleSend}
          />
          <BottomNav activeTab="tasks" />
        </div>
      </div>

      <Sidebar 
        isOpen={sidebarOpen} 
        onClose={() => setSidebarOpen(false)} 
        onSettingsClick={() => { setSidebarOpen(false); setSettingsOpen(true); }} userName={user?.displayName || user?.email?.split("@")[0]} userPhoto={user?.photoURL}
      />
      <SettingsPanel isOpen={settingsOpen} onClose={() => setSettingsOpen(false)} onSignOut={signOut} />
    </div>
  );
}
